import { desktopBridge } from "./desktop";

/** Only a phone browser suspends a hidden page. A desktop window keeps capturing and playing
 * audio while it is minimised or covered by another app, and treating that as a suspension
 * would stop what the person says from reaching the bot while the model still hears it.
 * Лише телефон призупиняє приховану сторінку; вікно десктопа далі чує й говорить, тож
 * вважати його призупиненим означає губити слова, які модель чує, а бот ні. */
export function backgroundsWhenHidden(desktop: unknown = desktopBridge()): boolean {
  return desktop === undefined;
}

/** Best-effort screen wake lock for an active call, never a background recording guarantee.
 * За можливості тримаємо екран увімкненим під час розмови; фоновий запис це не гарантує. */
export function bindLiveVoicePage(
  onVisibility: (backgrounded: boolean) => void,
  page: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener"> = document,
  wakeLock: Pick<WakeLock, "request"> | undefined = navigator.wakeLock,
): () => void {
  let active = true;
  let pending = false;
  let visibleWhilePending = false;
  let sentinel: WakeLockSentinel | null = null;
  const release = () => {
    const previous = sentinel;
    sentinel = null;
    if (previous) void previous.release().catch(() => undefined);
  };
  const acquire = async () => {
    if (!active || pending || sentinel || !wakeLock || page.visibilityState !== "visible") return;
    pending = true;
    try {
      const next = await wakeLock.request("screen");
      if (!active || page.visibilityState !== "visible") {
        await next.release();
      } else {
        sentinel = next;
        next.addEventListener(
          "release",
          () => {
            if (sentinel === next) sentinel = null;
          },
          { once: true },
        );
      }
    } catch {
      // Unsupported or refused wake locks must not stop a call.
      // Відмова системи утримувати екран не повинна завершувати розмову.
    } finally {
      pending = false;
      if (visibleWhilePending) {
        visibleWhilePending = false;
        void acquire();
      }
    }
  };
  const changed = () => {
    const hidden = page.visibilityState !== "visible";
    onVisibility(hidden);
    if (hidden) release();
    else if (pending) visibleWhilePending = true;
    else void acquire();
  };
  page.addEventListener("visibilitychange", changed);
  changed();
  return () => {
    active = false;
    page.removeEventListener("visibilitychange", changed);
    release();
  };
}
